import React, { useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import theme from "../../../theme/theme";
import {
  capitalizeFirstLetter,
  getBaraStatusColor,
} from "../../../utils/strings";
import i18n from "../../../i18n-config";
import {
  CustomButton,
  CustomPopper,
  CustomTableFooter,
  NoDataTable,
  StyledTableCell,
  StyledTableRow,
  SvgContainer,
} from "../../../components";
import { MoreVert } from "@mui/icons-material";
import { LOCATION, SORT } from "../../../assets/icons";
import { BARA_STATUS } from "../../../utils/constants";

const style = {
  fontSize: 14,
  color: theme.colors.black,
  fontWeight: 550,
};

const MenuProps = {
  PaperProps: {
    style: {
      paddingRight: 20,
    },
  },
};

interface Props {
  data: any;
  onDetail: (item: any) => void;
}

function BaraTable({ data, onDetail }: Props) {
  const [page, setPage] = useState(0);
  const [show, setShow] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const handleShowOpen = () => {
    setShow((prev) => !prev);
  };

  const items = (
    rowsPerPage > 0
      ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : data
  ).filter((val: any) =>
    ["", "ALL"].includes(selectedStatus)
      ? val
      : val.statutGeneral === selectedStatus
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ mb: 2, boxShadow: "0px 4px 4px rgba(139, 139, 139, 0.05)" }}
    >
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow sx={{ bgcolor: "#fff" }}>
            <TableCell sx={style}>#</TableCell>
            <TableCell sx={style}>
              <Stack direction={"row"} alignItems="center">
                <Typography mr={1}>
                  {capitalizeFirstLetter(i18n.t("nom_bara"))}
                </Typography>
                <IconButton>
                  <SvgContainer
                    svg={SORT}
                    width={15}
                    color={theme.colors.black}
                  />
                </IconButton>
              </Stack>
            </TableCell>
            <TableCell sx={style}>
              <Typography>
                {capitalizeFirstLetter(i18n.t("categorie_souscat"))}
              </Typography>
            </TableCell>
            <TableCell sx={style}>
              <Stack direction={"row"}>
                <SvgContainer
                  svg={LOCATION}
                  width={15}
                  color={theme.colors.dimgray + "80"}
                />
                <Typography ml={1}>
                  {capitalizeFirstLetter(i18n.t("adress_bara"))}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell sx={style}>
              <Stack direction={"row"} alignItems="center">
                <Typography mr={1}>
                  {capitalizeFirstLetter(i18n.t("date_besoin"))}
                </Typography>
                <IconButton>
                  <SvgContainer
                    svg={SORT}
                    width={15}
                    color={theme.colors.black}
                  />
                </IconButton>
              </Stack>
            </TableCell>
            <TableCell>
              <FormControl fullWidth>
                <InputLabel>{i18n.t("status")}</InputLabel>
                <Select variant="standard" value={selectedStatus}>
                  <MenuItem
                    value={"ALL"}
                    onClick={() => onStatusChange("ALL")}
                    style={{
                      background: "white",
                      color: theme.colors.black,
                    }}
                  >
                    {capitalizeFirstLetter(i18n.t("tout_afficher"))}
                  </MenuItem>
                  {BARA_STATUS.map((status: string) => (
                    <MenuItem
                      value={status}
                      onClick={() => onStatusChange(status)}
                      style={{
                        background: "white",
                        color: theme.colors.black,
                      }}
                    >
                      {capitalizeFirstLetter(i18n.t(status))}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
            <TableCell sx={style}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item: any, index: any) => (
            <StyledTableRow
              key={index}
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                cursor: "pointer",
              }}
            >
              <StyledTableCell>{item.id}</StyledTableCell>
              <StyledTableCell>{item.titre}</StyledTableCell>
              <StyledTableCell>{item.categorie?.libelle}</StyledTableCell>
              <StyledTableCell>{item.adresse}</StyledTableCell>
              <StyledTableCell>
                {new Date(item.dateCreation).toLocaleDateString("en-CA")}
              </StyledTableCell>
              <StyledTableCell style={{ width: 100 }}>
                <CustomPopper
                  icon={
                    <CustomButton
                      title={i18n.t(item.statutGeneral)}
                      rootStyle={{
                        backgroundColor: getBaraStatusColor(item.statutGeneral),
                        width: "100%",
                      }}
                      titleStyle={{ fontSize: 12, color: "white" }}
                      disabled
                    />
                  }
                  placement="center"
                  containerStyle={{ padding: 0, borderRadius: "5px" }}
                  buttonStyle={{
                    backgroundColor: "transparent",
                    width: "100%",
                    padding: 0,
                  }}
                >
                  <Box
                    p={2}
                    sx={{
                      height: "250px",
                      width: "200px",
                      position: "relative",
                      overflow: "auto",
                    }}
                  >
                    {BARA_STATUS.map((status: string) => (
                      <ListItem
                        value={status}
                        divider
                        onClick={() => onStatusChange(status)}
                        style={{
                          cursor: "pointer",
                          backgroundColor:
                            item.statutGeneral === status
                              ? "#c2c2c2"
                              : "transparent",
                        }}
                      >
                        {capitalizeFirstLetter(i18n.t(status))}
                      </ListItem>
                    ))}
                  </Box>
                </CustomPopper>
              </StyledTableCell>
              <StyledTableCell style={{ width: 40 }}>
                <Select
                  MenuProps={MenuProps}
                  disableUnderline
                  variant="standard"
                  IconComponent={(props) => (
                    <MoreVert
                      {...props}
                      sx={{ border: "1px solid #EAEAEA", borderRadius: 100 }}
                    />
                  )}
                >
                  <MenuItem
                    onClick={() => onDetail(item)}
                    style={{
                      background: "white",
                      color: theme.colors.black,
                    }}
                  >
                    {capitalizeFirstLetter(i18n.t("details_bara"))}
                  </MenuItem>
                </Select>
              </StyledTableCell>
            </StyledTableRow>
          ))}
          {!items.length ? <NoDataTable /> : null}
        </TableBody>
        <CustomTableFooter
          rowsPerPage={rowsPerPage}
          data={data}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Table>
    </TableContainer>
  );
}

export default BaraTable;
